import { isAxiosError } from 'axios';
import { PlanCode } from '../types';

const PROBLEM_TYPE_BASE = 'https://onetasklist.com/problems/';

export const BillingProblemTypes = {
  planLimitExceeded: `${PROBLEM_TYPE_BASE}plan-limit-exceeded`,
  unavailable: `${PROBLEM_TYPE_BASE}billing-unavailable`,
  busy: `${PROBLEM_TYPE_BASE}billing-busy`,
} as const;

/** The `error` values the OAuth callbacks put on `/auth/popup/callback` when billing stops a new connection. */
export const BillingPopupErrors = {
  planLimitExceeded: 'plan_limit_exceeded',
  unavailable: 'billing_unavailable',
} as const;

/** Which limit was hit. */
export type PlanLimitKind = 'boards' | 'professionalSources';

/** The body of a 402 `plan-limit-exceeded` ProblemDetails. */
export interface PlanLimitError {
  limit: PlanLimitKind;
  planCode: PlanCode;
  max: number;
  current: number;
  /** Set when a source connection was refused. */
  sourceTypeId?: string;
}

/** A billing refusal a caller can show: a limit to upgrade past, or a "try again" outage. */
export type BillingErrorKind = 'planLimit' | 'unavailable';

/** Why a billing action (checkout, portal) failed: Login is down or busy ("try again"), or anything else. */
export type BillingActionError = 'unavailable' | 'failed';

interface ProblemBody {
  type?: unknown;
  limit?: unknown;
  planCode?: unknown;
  max?: unknown;
  current?: unknown;
  sourceTypeId?: unknown;
}

const problemOf = (error: unknown, status: number): ProblemBody | null => {
  if (!isAxiosError(error) || error.response?.status !== status) return null;
  const data: unknown = error.response.data;
  return data !== null && typeof data === 'object' ? (data as ProblemBody) : null;
};

/**
 * Reads a 402 `plan-limit-exceeded` response. Returns null for anything else, including a 402 without
 * the problem type.
 */
export function parsePlanLimitError(error: unknown): PlanLimitError | null {
  const problem = problemOf(error, 402);
  if (!problem || problem.type !== BillingProblemTypes.planLimitExceeded) return null;
  if (problem.limit !== 'boards' && problem.limit !== 'professionalSources') return null;

  return {
    limit: problem.limit,
    planCode: (typeof problem.planCode === 'string' ? problem.planCode : 'free') as PlanCode,
    max: typeof problem.max === 'number' ? problem.max : 0,
    current: typeof problem.current === 'number' ? problem.current : 0,
    sourceTypeId: typeof problem.sourceTypeId === 'string' ? problem.sourceTypeId : undefined,
  };
}

/** True for the 503s billing returns when Login can't be reached or the account is busy: "try again". */
export function isBillingUnavailable(error: unknown): boolean {
  const problem = problemOf(error, 503);
  return problem?.type === BillingProblemTypes.unavailable || problem?.type === BillingProblemTypes.busy;
}

/** Classifies an API error as a billing refusal, or null when it's some other failure. */
export function getBillingErrorKind(error: unknown): BillingErrorKind | null {
  if (parsePlanLimitError(error)) return 'planLimit';
  if (isBillingUnavailable(error)) return 'unavailable';
  return null;
}

/** Classifies the `error` query value of the OAuth popup callback. */
export function getBillingPopupErrorKind(popupError: string | null | undefined): BillingErrorKind | null {
  if (popupError === BillingPopupErrors.planLimitExceeded) return 'planLimit';
  if (popupError === BillingPopupErrors.unavailable) return 'unavailable';
  return null;
}

/** Classifies a failed checkout or portal request. */
export function getBillingActionError(error: unknown): BillingActionError {
  return isBillingUnavailable(error) ? 'unavailable' : 'failed';
}

/**
 * A billing refusal for the UI to show: an upgrade prompt for a plan limit (with its details when the API
 * gave them; an OAuth popup only says which kind), or a "try again" for an outage.
 */
export type BillingRefusal =
  | { kind: 'planLimit'; limit: PlanLimitError | null }
  | { kind: 'unavailable' };

/** The refusal behind a failed API call, or null when the failure isn't billing's. */
export function getBillingRefusal(error: unknown): BillingRefusal | null {
  const limit = parsePlanLimitError(error);
  if (limit) return { kind: 'planLimit', limit };
  if (isBillingUnavailable(error)) return { kind: 'unavailable' };
  return null;
}

/** The refusal behind an OAuth popup's `error`, or null when it's another error. */
export function getBillingRefusalFromPopupError(popupError: string | null | undefined): BillingRefusal | null {
  const kind = getBillingPopupErrorKind(popupError);
  if (kind === 'planLimit') return { kind: 'planLimit', limit: null };
  if (kind === 'unavailable') return { kind: 'unavailable' };
  return null;
}
